import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AppLocalizationService } from '../../../../services/AppLocalizationService';

/**
 * Numeric field that displays values ***IN RD LOCALE***
 */
@Component({
  selector: 'pollux-report-designer-numeric-field',
  templateUrl: './pollux-report-designer-numeric-field.component.html',
  styleUrls: ['./pollux-report-designer-numeric-field.component.scss']
})
export class PolluxReportDesignerNumericFieldComponent implements OnInit {

  /**
   * Allow null values?
   */
  @Input() isNullable: boolean;

  /**
   * Minimum value
   */
  @Input() ngMinValue: number;

  /**
   * Maximum value
   */
  @Input() ngMaxValue: number;

  /**
   * Read-only
   */
  @Input() ngReadonly: boolean;

  /**
   * Show thousands separators
   */
  @Input() thousandsSeparators: boolean;

  /**
   * Amount of decimal places
   */
  @Input() decimals: number;

  /**
   * ID for HTML
   */
  @Input() ngId: string;

  /**
   * Initial value
   */
  private initialValue: number;

  /**
   * Internal model
   */
  public internalModel: string;

  /**
   * Sets the value
   */
  @Input() set value(value: number) {
    if (!this.initialized) {
      this.initialValue = value;//will be set to internalModel in ngOnInit (when all parameters are bound (such as decimals etc))
    } else {
      this.setInternalModel(value);
    }
  }

  /**
   * Gets the value
   */
  get value(): number {
    return this.internalModel ? this.getExternalValue() : null;
  }

  /**
   * Flag indicating that component is initialized
   */
  private initialized = false;

  /**
   * Sets the internal model
   */
  private setInternalModel(value: number) {
    this.internalModel = this.format(value);
    this.ref.markForCheck();
  }

  /**
   * Gets the external value (display)
   * @returns
   */
  private getExternalValue(): number {
    const parsedValue = this.parse(this.internalModel);
    const externalValue = parsedValue === null ? null : +parsedValue.toFixed(this.decimals);
    return externalValue;
  }

  /**
   * Parses the value
   * @param value
   * @returns
   */
  private parse(value: string): number | null {
    if (value === null || value.length === 0) {
      return null;
    }

    const decimalsSeparator = this.appLocalization.getReportDesignerDecimalsSeparator();
    const thousandsSeparator = this.appLocalization.getReportDesignerThousandsSeparator();
    if (decimalsSeparator === '.') {
      const parseFromValue = value.split(thousandsSeparator).join('');
      const ret = parseFloat(parseFromValue);
      return ret;
    } else {
      const parseFromValue = value.split(thousandsSeparator).join('').split(decimalsSeparator).join('.');
      const ret = parseFloat(parseFromValue);
      return ret;
    }
  }

  /**
   * Formats the value
   * @param value
   * @returns
   */
  private format(value: number): string {
    if (value === null) {
      return null;
    }
    let valueCheck;

    if (this.appLocalization.getReportDesignerDecimalsSeparator() === '.') {
      valueCheck = parseFloat(value.toString());
    }
    else {
      valueCheck = parseFloat(value.toString().split(',').join('.'));
    }

    const numActualDecimals = this.getDecimals(valueCheck);
    const numUseDecimals = Math.min(numActualDecimals, this.decimals);

    let ret = valueCheck.toFixed(numUseDecimals);

    if (this.appLocalization.getReportDesignerDecimalsSeparator() === ',') {
      ret = ret.split('.').join(',');
    }
    return ret;
  }

  /**
   * Get the amount of decimals
   * @param value
   * @returns
   */
  private getDecimals(value: number): number {
    if (value === null) {
      return 0;
    }
    if (Number.isInteger(value)) {
      return 0;
    }
    for (let i = 1; i < 15; ++i) {
      if (Number.isInteger(value * Math.pow(10, i))) {
        return i;
      }
    }
    return 0;
  }

  /**
   * value change emitter
   */
  @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();

  /**
   * Constructor
   * @param ref
   * @param appLocalization
   */
  constructor(private ref: ChangeDetectorRef, private appLocalization: AppLocalizationService) { }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initialized = true;
    this.setInternalModel(this.initialValue || null);
  }

  /**
   * Model changed event handler
   */
  public modelChanged() {
    const externalValue = this.getExternalValue();
    this.valueChange.next(externalValue);
    this.ref.markForCheck();
  }
}
